import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizedMessage } from "../i18n/LocaleContext";
import {
  EMPTY_FAVORITES,
  FavoriteSource,
  PabxContactItem,
  PabxFavorite,
  TeamsContactItem,
  TeamsFavorite,
  UserFavorites,
} from "../types/contacts";
import {
  FavoritesError,
  addFavorite as addFavoriteRequest,
  fetchFavorites,
  removeFavorite as removeFavoriteRequest,
  syncFavorites,
} from "../utils/favoritesApi";
import {
  pabxContactToFavorite,
  pabxFavoriteDiffers,
  teamsContactToFavorite,
  teamsFavoriteDiffers,
} from "../utils/favoriteMapping";
import { AuthorizedFetch } from "../utils/tenantAdminApi";

/**
 * Estado dos contatos favoritos: cópia de sessão do que está no backend.
 *
 * Carregado por `GET /userFavorites` quando o hook monta — rota própria, e não carona na resposta
 * do check-in, justamente para que recarregar a lista seja possível sem refazer o check-in (que
 * reescreve a configuração e pode derrubar o softphone).
 *
 * Cada toggle atualiza o estado **otimisticamente** e adota a lista que o servidor devolve, que
 * já incorpora o que outro dispositivo do mesmo usuário tenha gravado. O teto também vem do
 * servidor (`max`): o valor local só adianta o aviso na interface, e o 409 continua sendo tratado,
 * porque é o que chega quando outro dispositivo ocupou a última vaga.
 */

/**
 * Acesso à array de uma origem com um tipo comum. As duas arrays têm formatos diferentes de
 * propósito (cada origem guarda os campos que tem), e indexar `favorites[source]`
 * genericamente produz a união dos dois tipos de array, cujos métodos o TypeScript não
 * consegue combinar. Aqui só o `id` interessa.
 */
function listOf(favorites: UserFavorites, source: FavoriteSource): { id: string }[] {
  return source === "pabx" ? favorites.pabx : favorites.teams;
}

/** Aplicação otimista do toggle, por origem, para preservar os tipos de cada array. */
function withToggle(
  previous: UserFavorites,
  source: FavoriteSource,
  id: string,
  item: PabxFavorite | TeamsFavorite | null,
  marked: boolean
): UserFavorites {
  if (source === "pabx") {
    return {
      ...previous,
      pabx: marked
        ? previous.pabx.filter((favorite) => favorite.id !== id)
        : [...previous.pabx, item as PabxFavorite],
    };
  }
  return {
    ...previous,
    teams: marked
      ? previous.teams.filter((favorite) => favorite.id !== id)
      : [...previous.teams, item as TeamsFavorite],
  };
}

export type FavoritesState = {
  favorites: UserFavorites;
  max: number;
  total: number;
  isFull: boolean;
  loading: boolean;
  /** Erro da última operação, já pronto para tradução. Limpo no próximo toggle bem-sucedido. */
  error: LocalizedMessage | null;
  isFavorite: (source: FavoriteSource, id: string | null | undefined) => boolean;
  togglePabxFavorite: (item: PabxContactItem) => Promise<void>;
  toggleTeamsFavorite: (item: TeamsContactItem) => Promise<void>;
  /** Recarrega do backend (botão Atualizar, repetição depois de uma falha). */
  reload: () => Promise<void>;
  reconcileTeams: (contacts: TeamsContactItem[], options: { complete: boolean }) => Promise<void>;
  reconcilePabx: (contacts: PabxContactItem[]) => Promise<void>;
  clearError: () => void;
};

export function useFavorites(authorizedFetch: AuthorizedFetch | undefined, enabled: boolean): FavoritesState {
  const [favorites, setFavorites] = useState<UserFavorites>(EMPTY_FAVORITES);
  const [max, setMax] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LocalizedMessage | null>(null);

  // Último estado conhecido, para o rollback e para a reconciliação não depender do fechamento
  // (closure) de um render antigo.
  const favoritesRef = useRef<UserFavorites>(EMPTY_FAVORITES);
  favoritesRef.current = favorites;

  // Uma operação em voo por id: dois cliques rápidos na mesma estrela chegariam fora de ordem e o
  // estado final seria o da resposta mais lenta.
  const inFlightRef = useRef<Set<string>>(new Set());
  const disposedRef = useRef(false);

  useEffect(() => () => {
    disposedRef.current = true;
  }, []);

  const apply = useCallback((payload: { favorites: UserFavorites; max: number }) => {
    if (disposedRef.current) return;
    setFavorites({
      pabx: payload.favorites?.pabx ?? [],
      teams: payload.favorites?.teams ?? [],
    });
    setMax(payload.max);
  }, []);

  const reload = useCallback(async () => {
    if (!authorizedFetch) return;
    setLoading(true);
    try {
      apply(await fetchFavorites(authorizedFetch));
      if (!disposedRef.current) setError(null);
    } catch (requestError) {
      if (disposedRef.current) return;
      setError(requestError instanceof FavoritesError
        ? requestError.message$
        : { key: "contactSearch.favorites.error.generic" });
    } finally {
      if (!disposedRef.current) setLoading(false);
    }
  }, [apply, authorizedFetch]);

  // Carga inicial. Não bloqueia nada: a estrela só importa quando o usuário abre os contatos.
  useEffect(() => {
    if (!enabled || !authorizedFetch) return;
    void reload();
  }, [enabled, authorizedFetch, reload]);

  const isFavorite = useCallback(
    (source: FavoriteSource, id: string | null | undefined) =>
      !!id && listOf(favorites, source).some((favorite) => favorite.id === id),
    [favorites]
  );

  const total = favorites.pabx.length + favorites.teams.length;
  // `max` é 0 até a primeira resposta chegar; antes dela não há como afirmar que a lista está
  // cheia, e desabilitar a estrela por engano seria pior que um 409 no clique.
  const isFull = max > 0 && total >= max;

  /**
   * Marca ou desmarca, otimista. O estado volta ao anterior se a requisição falhar — inclusive no
   * 409, que é o caso de outro dispositivo ter ocupado a última vaga enquanto esta interface ainda
   * mostrava espaço livre.
   */
  const toggle = useCallback(
    async (source: FavoriteSource, id: string, item: PabxFavorite | TeamsFavorite | null) => {
      if (!authorizedFetch || !id) return;

      const key = `${source}:${id}`;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);

      const previous = favoritesRef.current;
      const marked = listOf(previous, source).some((favorite) => favorite.id === id);
      if (!marked && !item) {
        inFlightRef.current.delete(key);
        return;
      }

      setFavorites(withToggle(previous, source, id, item, marked));

      try {
        const payload = marked
          ? await removeFavoriteRequest(authorizedFetch, source, id)
          : await addFavoriteRequest(authorizedFetch, source, item as PabxFavorite | TeamsFavorite);
        apply(payload);
        if (!disposedRef.current) setError(null);
      } catch (requestError) {
        if (!disposedRef.current) {
          setFavorites(previous);
          setError(requestError instanceof FavoritesError
            ? requestError.message$
            : { key: "contactSearch.favorites.error.generic" });
        }
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [apply, authorizedFetch]
  );

  const togglePabxFavorite = useCallback(
    (item: PabxContactItem) => toggle("pabx", item.id || "", pabxContactToFavorite(item)),
    [toggle]
  );

  const toggleTeamsFavorite = useCallback(
    (item: TeamsContactItem) => toggle("teams", item.id || "", teamsContactToFavorite(item)),
    [toggle]
  );

  /**
   * Reconciliação do Teams: atualiza a cópia dos contatos que mudaram e **remove** os favoritos
   * cujo id desapareceu da agenda.
   *
   * `complete` é a autorização para concluir ausência, e quem a calcula é o chamador: a carga tem
   * de ter terminado por falta de `@odata.nextLink`, sem truncamento pelo teto de contatos, sem
   * erro em nenhuma página e **sem filtro** na requisição. Sem isso, só atualiza — uma lista
   * parcial não prova que um contato deixou de existir. Uma lista vazia, aí sim, zera os
   * favoritos: `200` sem recorte é resposta a ser levada a sério, e sem isso quem apagasse todos
   * os contatos ficaria com favoritos fantasma para sempre.
   */
  const reconcileTeams = useCallback(
    async (contacts: TeamsContactItem[], options: { complete: boolean }) => {
      if (!authorizedFetch) return;

      const stored = favoritesRef.current.teams;
      if (stored.length === 0) return;

      const byId = new Map<string, TeamsContactItem>();
      for (const contact of contacts) {
        if (contact.id) byId.set(contact.id, contact);
      }

      const items: TeamsFavorite[] = [];
      const removeIds: string[] = [];
      for (const favorite of stored) {
        const contact = byId.get(favorite.id);
        if (!contact) {
          if (options.complete) removeIds.push(favorite.id);
          continue;
        }
        if (teamsFavoriteDiffers(favorite, contact)) {
          const fresh = teamsContactToFavorite(contact);
          if (fresh) items.push(fresh);
        }
      }

      if (items.length === 0 && removeIds.length === 0) return;

      try {
        apply(await syncFavorites(authorizedFetch, "teams", items, removeIds));
      } catch {
        // Falha de reconciliação não é erro do usuário: mantém o estado anterior e tenta de novo
        // na próxima carga. Mostrar uma mensagem aqui seria ruído sobre algo que ele não pediu.
      }
    },
    [apply, authorizedFetch]
  );

  /**
   * PABX: só atualização, **nunca** remoção. Sem busca por id na API do PABX, um favorito ausente
   * do resultado é indistinguível de um que simplesmente não casou com o termo digitado. A
   * atualização é oportunista — aproveita os contatos que uma busca trouxe.
   */
  const reconcilePabx = useCallback(
    async (contacts: PabxContactItem[]) => {
      if (!authorizedFetch) return;

      const stored = favoritesRef.current.pabx;
      if (stored.length === 0 || contacts.length === 0) return;

      const byId = new Map<string, PabxContactItem>();
      for (const contact of contacts) {
        if (contact.id) byId.set(contact.id, contact);
      }

      const items: PabxFavorite[] = [];
      for (const favorite of stored) {
        const contact = byId.get(favorite.id);
        if (contact && pabxFavoriteDiffers(favorite, contact)) {
          const fresh = pabxContactToFavorite(contact);
          if (fresh) items.push(fresh);
        }
      }

      if (items.length === 0) return;

      try {
        apply(await syncFavorites(authorizedFetch, "pabx", items, []));
      } catch {
        // Idem: silencioso de propósito.
      }
    },
    [apply, authorizedFetch]
  );

  const clearError = useCallback(() => setError(null), []);

  return useMemo(
    () => ({
      favorites,
      max,
      total,
      isFull,
      loading,
      error,
      isFavorite,
      togglePabxFavorite,
      toggleTeamsFavorite,
      reload,
      reconcileTeams,
      reconcilePabx,
      clearError,
    }),
    [
      favorites,
      max,
      total,
      isFull,
      loading,
      error,
      isFavorite,
      togglePabxFavorite,
      toggleTeamsFavorite,
      reload,
      reconcileTeams,
      reconcilePabx,
      clearError,
    ]
  );
}
